import * as React from "react";
import { Kebab, MenuItem } from "react-ui";

import {
    ROW_MENU_ADD_NEXT_CLICK,
    ROW_MENU_ADD_PREVIOUS_CLICK,
    ROW_MENU_COPY_LAST_CLICK,
    ROW_MENU_COPY_NEXT_CLICK,
    ROW_MENU_REMOVE_CLICK,
} from "../../metricsAction";
import { rowMenuTourStep as tourStep } from "../../FormParts/Tour/Steps/RowMenu";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { useTourStepRef } from "../../Internals/TourManagement/UseTourStepRef";
import { useClosestParentInstanceStrict } from "../../Internals/ControlContexts/PathContexts/PushInstance";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { Icon } from "../../Icons/Icon";

export interface RowMenuProps {
    addItemsDisabled: boolean;
    onCopyInstance: (bindingPath: AbsoluteResolvedModelPath, sourceInstance: string, instance?: string) => void;
    onRemoveInstance: (bindingPath: AbsoluteResolvedModelPath, instance: string) => void;
    onAddInstanceAfter: (bindingPath: AbsoluteResolvedModelPath, instance: string) => void;
    onAddInstanceBefore: (bindingPath: AbsoluteResolvedModelPath, instance: string) => void;
}

export function RowMenu(props: RowMenuProps): JSX.Element {
    const tourNodeRef = useTourStepRef(tourStep);
    const parentInstance = useClosestParentInstanceStrict();
    const track = useMetrics();

    const handleAddToNextClick = (): void => {
        track(ROW_MENU_ADD_NEXT_CLICK);
        props.onAddInstanceAfter(parentInstance.bindingPath, parentInstance.instance);
    };

    const handleAddToPreviousClick = (): void => {
        track(ROW_MENU_ADD_PREVIOUS_CLICK);
        props.onAddInstanceBefore(parentInstance.bindingPath, parentInstance.instance);
    };

    const handleCopyToNextClick = (): void => {
        track(ROW_MENU_COPY_NEXT_CLICK);
        const instance = parentInstance.instance;
        props.onCopyInstance(parentInstance.bindingPath, instance, instance);
    };

    const handleCopyToEndClick = (): void => {
        track(ROW_MENU_COPY_LAST_CLICK);
        props.onCopyInstance(parentInstance.bindingPath, parentInstance.instance);
    };

    const handleRemoveInstanceClick = (): void => {
        track(ROW_MENU_REMOVE_CLICK);
        props.onRemoveInstance(parentInstance.bindingPath, parentInstance.instance);
    };

    const pageObjectId = React.useMemo(
        () => parentInstance.bindingPath.joinWith(parentInstance.instance).toLegacyPath(),
        [parentInstance]
    );
    const testPageObject = {
        getType: () => "RowMenu",
        remove: handleRemoveInstanceClick,
        insert: handleCopyToNextClick,
        add: handleCopyToEndClick,
    };

    const iconSize = 14;

    /* NOTE
     * Анимация у Kebab отключена ибо приводит к станным warning-ам в консоли. Проверялось на react 16.14.0
     */
    return (
        <TestPageObjectHandle id={pageObjectId} pageObject={testPageObject}>
            <Kebab size="medium" disableAnimations ref={tourNodeRef}>
                <MenuItem
                    data-tid="row-menu-add-previous"
                    onClick={handleAddToPreviousClick}
                    disabled={props.addItemsDisabled}>
                    {/* @ts-ignore нет иконки новой не планируется*/}
                    <Icon name="ArrowADownIcon" size={iconSize} />
                    {" Добавить строку выше"}
                </MenuItem>
                <MenuItem data-tid="row-menu-add-next" onClick={handleAddToNextClick} disabled={props.addItemsDisabled}>
                    {/* @ts-ignore нет иконки новой не планируется*/}
                    <Icon name="ArrowAUpIcon" size={iconSize} />
                    {" Добавить строку ниже"}
                </MenuItem>
                <MenuItem data-tid="row-menu-copy" onClick={handleCopyToNextClick} disabled={props.addItemsDisabled}>
                    <Icon name="CopyIcon" size={iconSize} />
                    {" Скопировать после текущей"}
                </MenuItem>
                <MenuItem
                    data-tid="row-menu-copy-to-end"
                    onClick={handleCopyToEndClick}
                    disabled={props.addItemsDisabled}>
                    <Icon name="CopyIcon" size={iconSize} />
                    {" Скопировать в конец"}
                </MenuItem>
                <MenuItem data-tid="row-menu-remove" onClick={handleRemoveInstanceClick}>
                    <Icon name="TrashCanIcon" size={iconSize} />
                    {" Удалить"}
                </MenuItem>
            </Kebab>
        </TestPageObjectHandle>
    );
}
